import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { promoAdminAPI } from '../../services/api';
import { ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton } from '../../components/SuperAdmin/ListTools';
import { inDateRange, matchesSearch, formatExportDate } from '../../utils/exportData';
import {
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineX,
  HiCheck,
  HiOutlineTicket,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineCalendar,
} from 'react-icons/hi';

const inr = (n) => `₹${n.toLocaleString('en-IN')}`;

const defaultFormData = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: 0,
  maxDiscount: null,
  minOrderValue: 0,
  applicablePlans: [],
  applicableCycles: [],
  maxUses: null,
  maxUsesPerUser: 1,
  startsAt: '',
  expiresAt: '',
  isActive: true,
};

/** Status shown in the table: inactive / expired / scheduled / used up / active. */
const promoStatus = (promo) => {
  if (!promo.isActive) return 'inactive';
  if (promo.expiresAt && new Date(promo.expiresAt) < new Date()) return 'expired';
  if (promo.startsAt && new Date(promo.startsAt) > new Date()) return 'scheduled';
  if (promo.maxUses !== null && promo.maxUses !== undefined && promo.usedCount >= promo.maxUses) return 'used_up';
  return 'active';
};

const PROMO_STATUS_LABELS = {
  active: 'Active',
  scheduled: 'Scheduled',
  expired: 'Expired',
  used_up: 'Used up',
  inactive: 'Inactive',
};

const EMPTY_FILTERS = {
  search: '', status: '', discountType: '', cycle: '', createdFrom: '', createdTo: '', expiresFrom: '', expiresTo: '',
};

const PROMO_EXPORT_COLUMNS = [
  { header: 'Code', value: (p) => p.code },
  { header: 'Description', value: (p) => p.description },
  { header: 'Discount Type', value: (p) => (p.discountType === 'percentage' ? 'Percentage' : 'Fixed') },
  { header: 'Discount Value', value: (p) => p.discountValue },
  { header: 'Max Discount (₹)', value: (p) => p.maxDiscount ?? '' },
  { header: 'Min Order (₹)', value: (p) => p.minOrderValue || 0 },
  { header: 'Billing Cycles', value: (p) => (p.applicableCycles?.length ? p.applicableCycles : 'All') },
  { header: 'Applicable Plans', value: (p) => (p.applicablePlans?.length ? p.applicablePlans.map((pl) => pl?.name || pl) : 'All') },
  { header: 'Used', value: (p) => p.usedCount || 0 },
  { header: 'Max Uses', value: (p) => p.maxUses ?? 'Unlimited' },
  { header: 'Max Uses / User', value: (p) => p.maxUsesPerUser ?? '' },
  { header: 'Starts', value: (p) => formatExportDate(p.startsAt, false) },
  { header: 'Expires', value: (p) => (p.expiresAt ? formatExportDate(p.expiresAt, false) : 'Never') },
  { header: 'Status', value: (p) => PROMO_STATUS_LABELS[promoStatus(p)] },
  { header: 'Created', value: (p) => formatExportDate(p.createdAt) },
];

const PromocodesList = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const filtersActive = Object.keys(EMPTY_FILTERS).some((k) => filters[k]);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({ ...defaultFormData });
  const [formError, setFormError] = useState('');

  const { data: promosData, isLoading } = useQuery({
    queryKey: ['superadmin-promocodes'],
    queryFn: async () => {
      // Promo codes are a small set — load them all and filter on the client,
      // since the useful status (expired / scheduled / used up) is derived.
      const result = await promoAdminAPI.getAll({ limit: 500 });
      return result;
    },
  });

  const promos = useMemo(() => promosData?.data?.data || [], [promosData]);
  const filteredPromos = useMemo(
    () => promos.filter((p) =>
      matchesSearch(filters.search, p.code, p.description)
      && (!filters.status || promoStatus(p) === filters.status)
      && (!filters.discountType || p.discountType === filters.discountType)
      && (!filters.cycle || !p.applicableCycles?.length || p.applicableCycles.includes(filters.cycle))
      && inDateRange(p.createdAt, filters.createdFrom, filters.createdTo)
      && inDateRange(p.expiresAt, filters.expiresFrom, filters.expiresTo)),
    [promos, filters],
  );

  const createMutation = useMutation({
    mutationFn: promoAdminAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-promocodes']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to create promo code'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await promoAdminAPI.update(id, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-promocodes']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to update promo code'),
  });

  const deleteMutation = useMutation({
    mutationFn: promoAdminAPI.delete,
    onSuccess: () => queryClient.invalidateQueries(['superadmin-promocodes']),
  });

  const closeModal = () => {
    setShowModal(false);
    setEditingPromo(null);
    setFormError('');
    setFormData({ ...defaultFormData });
  };

  const openEditModal = (promo) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code || '',
      description: promo.description || '',
      discountType: promo.discountType || 'percentage',
      discountValue: promo.discountValue || 0,
      maxDiscount: promo.maxDiscount ?? '',
      minOrderValue: promo.minOrderValue || 0,
      applicablePlans: promo.applicablePlans || [],
      applicableCycles: promo.applicableCycles || [],
      maxUses: promo.maxUses ?? '',
      maxUsesPerUser: promo.maxUsesPerUser || 1,
      startsAt: promo.startsAt ? new Date(promo.startsAt).toISOString().slice(0, 16) : '',
      expiresAt: promo.expiresAt ? new Date(promo.expiresAt).toISOString().slice(0, 16) : '',
      isActive: promo.isActive !== undefined ? promo.isActive : true,
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === 'discountValue' || name === 'minOrderValue' || name === 'maxUsesPerUser') {
      setFormData((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else if (name === 'maxDiscount' || name === 'maxUses') {
      setFormData((prev) => ({ ...prev, [name]: value === '' ? '' : parseFloat(value) || '' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const toggleCycle = (cycle) => {
    setFormData((prev) => ({
      ...prev,
      applicableCycles: prev.applicableCycles.includes(cycle)
        ? prev.applicableCycles.filter((c) => c !== cycle)
        : [...prev.applicableCycles, cycle],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.code.trim()) {
      setFormError('Promo code is required');
      return;
    }
    if (formData.discountValue <= 0) {
      setFormError('Discount value must be greater than 0');
      return;
    }
    if (formData.discountType === 'percentage' && formData.discountValue > 100) {
      setFormError('Percentage discount cannot exceed 100%');
      return;
    }

    const payload = {
      ...formData,
      code: formData.code.trim().toUpperCase(),
      description: formData.description.trim(),
      maxDiscount: formData.maxDiscount === '' ? null : Number(formData.maxDiscount) || null,
      maxUses: formData.maxUses === '' ? null : Number(formData.maxUses) || null,
      minOrderValue: Number(formData.minOrderValue) || 0,
      startsAt: formData.startsAt || null,
      expiresAt: formData.expiresAt || null,
    };

    if (editingPromo) {
      updateMutation.mutate({ id: editingPromo._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (id, code) => {
    if (window.confirm(`Are you sure you want to deactivate "${code}"? This cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="promocodes-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>Promo Codes</h1>
          <p>Create and manage promotional codes for discounts</p>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <HiOutlinePlus className="w-4 h-4" />
            Create Promo Code
          </button>
        </div>
      </div>

      {/* Filters */}
      {promos.length > 0 && (
        <div className="filters-bar">
          <input
            type="text"
            placeholder="Search code or description..."
            value={filters.search}
            onChange={(e) => setFilter({ search: e.target.value })}
            className="search-input"
          />
          <FilterSelect
            value={filters.status}
            onChange={(v) => setFilter({ status: v })}
            allLabel="All Status"
            options={Object.entries(PROMO_STATUS_LABELS)}
          />
          <FilterSelect
            value={filters.discountType}
            onChange={(v) => setFilter({ discountType: v })}
            allLabel="All Discount Types"
            options={[['percentage', 'Percentage (%)'], ['fixed', 'Fixed Amount (₹)']]}
          />
          <FilterSelect
            value={filters.cycle}
            onChange={(v) => setFilter({ cycle: v })}
            allLabel="All Billing Cycles"
            options={[['monthly', 'Monthly'], ['yearly', 'Yearly']]}
          />
          <DateRangeFilter
            label="Created"
            from={filters.createdFrom}
            to={filters.createdTo}
            onChange={({ from, to }) => setFilter({ createdFrom: from, createdTo: to })}
          />
          <DateRangeFilter
            label="Expires"
            from={filters.expiresFrom}
            to={filters.expiresTo}
            onChange={({ from, to }) => setFilter({ expiresFrom: from, expiresTo: to })}
          />
          <ResetFiltersButton active={filtersActive} onReset={() => setFilters(EMPTY_FILTERS)} />
          <ExportMenu fileName="promo-codes" columns={PROMO_EXPORT_COLUMNS} getRows={() => filteredPromos} />
        </div>
      )}

      {/* Promo List */}
      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading promo codes...</p>
        </div>
      ) : promos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <HiOutlineTicket className="w-12 h-12 text-gray-300" />
          </div>
          <h3>No promo codes yet</h3>
          <p>Create your first promo code to offer discounts to users</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <HiOutlinePlus className="w-4 h-4" />
            Create Promo Code
          </button>
        </div>
      ) : filteredPromos.length === 0 ? (
        <div className="empty-state">
          <h3>No promo codes match these filters</h3>
          <p>Try a different search or reset the filters</p>
        </div>
      ) : (
        <div className="promos-table-wrapper">
          <table className="promos-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Min Order</th>
                <th>Usage</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPromos.map((promo) => {
                const isExpired = promo.expiresAt && new Date(promo.expiresAt) < new Date();
                const isNotStarted = promo.startsAt && new Date(promo.startsAt) > new Date();
                const isUsedUp = promo.maxUses !== null && promo.usedCount >= promo.maxUses;

                return (
                  <tr key={promo._id} className={!promo.isActive ? 'row-inactive' : ''}>
                    <td>
                      <div className="promo-code-cell">
                        <span className="promo-code-badge">{promo.code}</span>
                        {promo.description && <span className="promo-desc">{promo.description}</span>}
                      </div>
                    </td>
                    <td>
                      {promo.discountType === 'percentage'
                        ? `${promo.discountValue}% off`
                        : `${inr(promo.discountValue)} off`}
                      {promo.discountType === 'percentage' && promo.maxDiscount && (
                        <span className="promo-max-discount"> max {inr(promo.maxDiscount)}</span>
                      )}
                    </td>
                    <td>{promo.minOrderValue ? inr(promo.minOrderValue) : 'None'}</td>
                    <td>
                      {promo.usedCount}{promo.maxUses ? `/${promo.maxUses}` : ''}
                      {isUsedUp && <span className="used-up-badge">Used up</span>}
                    </td>
                    <td>
                      {promo.expiresAt ? (
                        <span className={isExpired ? 'text-red-500' : ''}>{formatDate(promo.expiresAt)}</span>
                      ) : (
                        <span className="text-gray-400">Never</span>
                      )}
                    </td>
                    <td>
                      {!promo.isActive ? (
                        <span className="status-badge status-inactive">Inactive</span>
                      ) : isExpired ? (
                        <span className="status-badge status-expired">Expired</span>
                      ) : isNotStarted ? (
                        <span className="status-badge status-scheduled">Scheduled</span>
                      ) : (
                        <span className="status-badge status-active">Active</span>
                      )}
                    </td>
                    <td>
                      <div className="action-btns">
                        <button className="btn btn-outline btn-sm" onClick={() => openEditModal(promo)}>
                          <HiOutlinePencil className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        {promo.isActive && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(promo._id, promo.code)}>
                            <HiOutlineTrash className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="promo-modal" onClick={(e) => e.stopPropagation()}>
            <div className="promo-modal__header">
              <div>
                <h2>{editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}</h2>
                <p className="promo-modal__subtitle">
                  {editingPromo ? `Modify ${editingPromo.code}` : 'Configure discount and usage limits'}
                </p>
              </div>
              <button className="promo-modal__close" onClick={closeModal}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="promo-modal__body">
              {formError && (
                <div className="promo-modal__error">
                  <HiOutlineX className="w-4 h-4" />
                  {formError}
                </div>
              )}

              <div className="promo-modal__grid promo-modal__grid--2col">
                <div className="promo-modal__field">
                  <label>Promo Code <span className="required">*</span></label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="e.g. LAUNCH20"
                    required
                    disabled={!!editingPromo}
                    className={editingPromo ? 'field-disabled' : ''}
                    style={{ textTransform: 'uppercase' }}
                  />
                  {editingPromo && <span className="field-hint">Code cannot be changed after creation</span>}
                </div>
                <div className="promo-modal__field">
                  <label>Discount Type <span className="required">*</span></label>
                  <select name="discountType" value={formData.discountType} onChange={handleInputChange}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
              </div>

              <div className="promo-modal__grid promo-modal__grid--3col">
                <div className="promo-modal__field">
                  <label>Discount Value <span className="required">*</span></label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      name="discountValue"
                      value={formData.discountValue}
                      onChange={handleInputChange}
                      min="0"
                      step={formData.discountType === 'percentage' ? '1' : '0.01'}
                      required
                    />
                    <span className="input-suffix">{formData.discountType === 'percentage' ? '%' : '₹'}</span>
                  </div>
                </div>
                <div className="promo-modal__field">
                  <label>Max Discount {formData.discountType === 'percentage' ? '(cap)' : ''}</label>
                  <input
                    type="number"
                    name="maxDiscount"
                    value={formData.maxDiscount}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="No limit"
                  />
                  <span className="field-hint">{formData.discountType === 'percentage' ? 'Max ₹ discount for % promos' : 'Not applicable for fixed'}</span>
                </div>
                <div className="promo-modal__field">
                  <label>Min Order Value</label>
                  <input
                    type="number"
                    name="minOrderValue"
                    value={formData.minOrderValue}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="0 = no minimum"
                  />
                </div>
              </div>

              <div className="promo-modal__field">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of this promo code"
                  rows={2}
                />
              </div>

              <div className="promo-modal__section-title">Applicability</div>

              <div className="promo-modal__field">
                <label>Applicable Billing Cycles</label>
                <div className="cycle-toggle-row">
                  <button
                    type="button"
                    className={`cycle-toggle-btn ${formData.applicableCycles.length === 0 ? 'cycle-toggle-btn--all' : ''}`}
                    onClick={() => setFormData((prev) => ({ ...prev, applicableCycles: [] }))}
                  >
                    All Cycles
                  </button>
                  <button
                    type="button"
                    className={`cycle-toggle-btn ${formData.applicableCycles.includes('monthly') ? 'cycle-toggle-btn--active' : ''}`}
                    onClick={() => toggleCycle('monthly')}
                  >
                    Monthly Only
                  </button>
                  <button
                    type="button"
                    className={`cycle-toggle-btn ${formData.applicableCycles.includes('yearly') ? 'cycle-toggle-btn--active' : ''}`}
                    onClick={() => toggleCycle('yearly')}
                  >
                    Yearly Only
                  </button>
                </div>
                <span className="field-hint">Leave "All Cycles" selected to apply to both monthly and yearly</span>
              </div>

              <div className="promo-modal__section-title">Usage Limits</div>

              <div className="promo-modal__grid promo-modal__grid--2col">
                <div className="promo-modal__field">
                  <label>Max Total Uses</label>
                  <input
                    type="number"
                    name="maxUses"
                    value={formData.maxUses}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="Unlimited"
                  />
                  <span className="field-hint">Leave empty for unlimited total uses</span>
                </div>
                <div className="promo-modal__field">
                  <label>Max Uses Per Organization</label>
                  <input
                    type="number"
                    name="maxUsesPerUser"
                    value={formData.maxUsesPerUser}
                    onChange={handleInputChange}
                    min="1"
                  />
                  <span className="field-hint">How many times each org can use this code</span>
                </div>
              </div>

              <div className="promo-modal__section-title">Schedule</div>

              <div className="promo-modal__grid promo-modal__grid--2col">
                <div className="promo-modal__field">
                  <label>Starts At</label>
                  <input
                    type="datetime-local"
                    name="startsAt"
                    value={formData.startsAt}
                    onChange={handleInputChange}
                  />
                  <span className="field-hint">Leave empty for immediate activation</span>
                </div>
                <div className="promo-modal__field">
                  <label>Expires At</label>
                  <input
                    type="datetime-local"
                    name="expiresAt"
                    value={formData.expiresAt}
                    onChange={handleInputChange}
                  />
                  <span className="field-hint">Leave empty for no expiration</span>
                </div>
              </div>

              <div className="promo-modal__field">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  {formData.isActive ? (
                    <><HiOutlineEye className="w-4 h-4" /> Active</>
                  ) : (
                    <><HiOutlineEyeOff className="w-4 h-4" /> Inactive</>
                  )}
                </label>
              </div>

              <div className="promo-modal__actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingPromo
                    ? 'Update Promo Code'
                    : 'Create Promo Code'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Scoped Styles ── */}
      <style>{`
        .promocodes-page { max-width: 100%; }
        .page-header {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 1.5rem;
        }
        .header-left h1 { font-size: 1.5rem; font-weight: 700; color: #111827; margin: 0; }
        .header-left p { font-size: 0.875rem; color: #6b7280; margin: 0.25rem 0 0; }
        .btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 0.375rem;
          padding: 0.5rem 1rem; font-size: 0.875rem; font-weight: 500; border-radius: 0.5rem;
          border: none; cursor: pointer; transition: all 0.15s; text-decoration: none;
        }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-primary { background: #6366f1; color: #fff; }
        .btn-primary:hover:not(:disabled) { background: #4f46e5; }
        .btn-outline { background: transparent; border: 1.5px solid #d1d5db; color: #374151; }
        .btn-outline:hover:not(:disabled) { background: #f9fafb; border-color: #9ca3af; }
        .btn-danger { background: #ef4444; color: #fff; }
        .btn-danger:hover:not(:disabled) { background: #dc2626; }
        .btn-sm { padding: 0.375rem 0.75rem; font-size: 0.75rem; }

        .loading-state { display: flex; flex-direction: column; align-items: center; gap: 1rem; padding: 3rem; color: #6b7280; }
        .loading-spinner {
          width: 2rem; height: 2rem; border: 3px solid #e5e7eb; border-top-color: #6366f1;
          border-radius: 50%; animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .empty-state {
          display: flex; flex-direction: column; align-items: center; gap: 0.75rem;
          padding: 4rem 2rem; text-align: center;
        }
        .empty-state h3 { font-size: 1.125rem; font-weight: 600; color: #374151; }
        .empty-state p { font-size: 0.875rem; color: #6b7280; }

        .promos-table-wrapper { overflow-x: auto; }
        .promos-table {
          width: 100%; border-collapse: collapse; font-size: 0.875rem;
        }
        .promos-table th {
          background: #f9fafb; padding: 0.75rem 1rem; text-align: left;
          font-size: 0.75rem; font-weight: 600; color: #6b7280; text-transform: uppercase;
          letter-spacing: 0.05em; border-bottom: 2px solid #e5e7eb;
        }
        .promos-table td {
          padding: 0.75rem 1rem; border-bottom: 1px solid #f3f4f6; color: #374151;
        }
        .promos-table tr.row-inactive { opacity: 0.55; }
        .promos-table tr:hover { background: #f9fafb; }

        .promo-code-cell { display: flex; flex-direction: column; gap: 0.125rem; }
        .promo-code-badge {
          display: inline-block; font-family: monospace; font-size: 0.875rem; font-weight: 700;
          background: #eef2ff; color: #4f46e5; padding: 0.125rem 0.5rem; border-radius: 0.25rem;
          letter-spacing: 0.05em;
        }
        .promo-desc { font-size: 0.75rem; color: #9ca3af; }
        .promo-max-discount { font-size: 0.6875rem; color: #059669; }
        .used-up-badge {
          display: inline-block; font-size: 0.625rem; font-weight: 600;
          background: #fef3c7; color: #92400e; padding: 0.0625rem 0.375rem; border-radius: 9999px;
          margin-left: 0.375rem;
        }

        .status-badge {
          display: inline-block; padding: 0.125rem 0.5rem; border-radius: 9999px;
          font-size: 0.6875rem; font-weight: 600;
        }
        .status-active { background: #dcfce7; color: #166534; }
        .status-inactive { background: #f3f4f6; color: #6b7280; }
        .status-expired { background: #fef2f2; color: #991b1b; }
        .status-scheduled { background: #fef3c7; color: #92400e; }

        .action-btns { display: flex; gap: 0.375rem; }
        .text-red-500 { color: #ef4444; }
        .text-gray-400 { color: #9ca3af; }

        .modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex;
          align-items: center; justify-content: center; z-index: 50; padding: 1rem;
        }
        .promo-modal {
          background: #fff; border-radius: 1rem; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
          width: 100%; max-width: 640px; max-height: 90vh; overflow-y: auto;
        }
        .promo-modal__header {
          display: flex; justify-content: space-between; align-items: flex-start;
          padding: 1.25rem 1.5rem; border-bottom: 1px solid #e5e7eb; background: #f9fafb;
        }
        .promo-modal__header h2 { font-size: 1.125rem; font-weight: 700; color: #111827; margin: 0; }
        .promo-modal__subtitle { font-size: 0.8125rem; color: #6b7280; margin: 0.25rem 0 0; }
        .promo-modal__close {
          background: transparent; border: none; padding: 0.375rem; border-radius: 0.5rem;
          color: #9ca3af; cursor: pointer; transition: all 0.15s;
        }
        .promo-modal__close:hover { background: #e5e7eb; color: #374151; }
        .promo-modal__body { padding: 1.5rem; }
        .promo-modal__error {
          display: flex; align-items: center; gap: 0.5rem; background: #fef2f2; color: #991b1b;
          padding: 0.75rem 1rem; border-radius: 0.5rem; margin-bottom: 1rem; font-size: 0.875rem;
          border: 1px solid #fecaca;
        }
        .promo-modal__section-title {
          font-size: 0.75rem; font-weight: 600; color: #6b7280; text-transform: uppercase;
          letter-spacing: 0.05em; margin: 1.25rem 0 0.75rem; padding-top: 0.75rem;
          border-top: 1px solid #f3f4f6;
        }
        .promo-modal__grid--2col { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
        .promo-modal__grid--3col { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1rem; }
        @media (max-width: 640px) {
          .promo-modal__grid--2col, .promo-modal__grid--3col { grid-template-columns: 1fr; }
        }
        .promo-modal__field { margin-bottom: 1rem; }
        .promo-modal__field label {
          display: block; font-size: 0.8125rem; font-weight: 600; color: #374151; margin-bottom: 0.375rem;
        }
        .promo-modal__field .required { color: #ef4444; }
        .promo-modal__field input,
        .promo-modal__field textarea,
        .promo-modal__field select {
          width: 100%; padding: 0.5rem 0.75rem; font-size: 0.875rem; border: 1.5px solid #d1d5db;
          border-radius: 0.5rem; background: #fff; transition: all 0.15s; color: #111827;
        }
        .promo-modal__field input:focus,
        .promo-modal__field textarea:focus,
        .promo-modal__field select:focus {
          outline: none; border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
        }
        .promo-modal__field input.field-disabled { background: #f3f4f6; color: #9ca3af; cursor: not-allowed; }
        .promo-modal__field textarea { resize: vertical; min-height: 60px; }
        .field-hint { display: block; font-size: 0.6875rem; color: #9ca3af; margin-top: 0.25rem; }

        .input-with-suffix { display: flex; align-items: center; gap: 0; }
        .input-with-suffix input { flex: 1; border-top-right-radius: 0; border-bottom-right-radius: 0; }
        .input-suffix {
          padding: 0.5rem 0.75rem; font-size: 0.875rem; font-weight: 600; color: #6b7280;
          background: #f9fafb; border: 1.5px solid #d1d5db; border-left: none;
          border-top-right-radius: 0.5rem; border-bottom-right-radius: 0.5rem;
        }

        .cycle-toggle-row { display: flex; gap: 0.5rem; flex-wrap: wrap; }
        .cycle-toggle-btn {
          padding: 0.375rem 0.75rem; border-radius: 0.5rem; font-size: 0.8125rem; font-weight: 500;
          border: 1.5px solid #d1d5db; background: #fff; color: #374151; cursor: pointer; transition: all 0.15s;
        }
        .cycle-toggle-btn:hover { border-color: #6366f1; color: #6366f1; }
        .cycle-toggle-btn--all { background: #eef2ff; border-color: #6366f1; color: #6366f1; }
        .cycle-toggle-btn--active { background: #6366f1; border-color: #6366f1; color: #fff; }

        .toggle-label {
          display: flex !important; align-items: center; gap: 0.5rem; font-size: 0.875rem;
          color: #374151; cursor: pointer; padding: 0.75rem 1rem; border-radius: 0.5rem;
          border: 1.5px solid #e5e7eb; transition: all 0.15s;
        }
        .toggle-label:hover { background: #f9fafb; border-color: #d1d5db; }
        .toggle-label input[type="checkbox"] { width: 1rem; height: 1rem; accent-color: #6366f1; }

        .promo-modal__actions {
          display: flex; justify-content: flex-end; gap: 0.75rem; padding-top: 1.25rem;
          border-top: 1px solid #e5e7eb; margin-top: 1rem;
        }

        /* ===== Dark theme — promo codes table & page ===== */
        html.dark .header-left h1 { color: #f1f5f9; }
        html.dark .header-left p { color: #8ea1b8; }
        html.dark .empty-state h3 { color: #cbd5e1; }
        html.dark .empty-state p { color: #8ea1b8; }
        html.dark .promos-table th {
          background: #131833;
          color: #aebdce;
          border-bottom: 2px solid #2a3158;
        }
        html.dark .promos-table td {
          color: #cbd5e1;
          border-bottom: 1px solid #1a2140;
        }
        html.dark .promos-table tr:hover { background: #243060; }
        html.dark .promo-code-badge {
          background: rgba(139, 92, 246, 0.18);
          color: #a5b4fc;
        }
        html.dark .promo-desc { color: #8ea1b8; }
        html.dark .promo-max-discount { color: #34d399; }
        html.dark .used-up-badge {
          background: rgba(245, 158, 11, 0.18);
          color: #fbbf24;
        }
        html.dark .status-active { background: rgba(34, 197, 94, 0.18); color: #4ade80; }
        html.dark .status-inactive { background: #1a2140; color: #aebdce; }
        html.dark .status-expired { background: rgba(239, 68, 68, 0.18); color: #f87171; }
        html.dark .status-scheduled { background: rgba(245, 158, 11, 0.18); color: #fbbf24; }
        html.dark .btn-outline {
          border-color: #2a3158;
          color: #cbd5e1;
        }
        html.dark .btn-outline:hover:not(:disabled) {
          background: #151c38;
          border-color: #384070;
        }

        /* Dark: promo modal (same navy family as the rest of the panel) */
        html.dark .promo-modal {
          background: #161c36;
          border: 1px solid #2a3158;
          box-shadow: none;
        }
        html.dark .promo-modal__header {
          background: #131833;
          border-bottom-color: #2a3158;
        }
        html.dark .promo-modal__header h2 { color: #f1f5f9; }
        html.dark .promo-modal__subtitle { color: #8ea1b8; }
        html.dark .promo-modal__close { color: #8ea1b8; }
        html.dark .promo-modal__close:hover { background: #1a2140; color: #f1f5f9; }
        html.dark .promo-modal__error {
          background: rgba(239, 68, 68, 0.15);
          color: #fca5a5;
          border-color: rgba(248, 113, 113, 0.4);
        }
        html.dark .promo-modal__section-title {
          color: #8ea1b8;
          border-top-color: #2a3158;
        }
        html.dark .promo-modal__field label { color: #cbd5e1; }
        html.dark .promo-modal__field input,
        html.dark .promo-modal__field textarea,
        html.dark .promo-modal__field select {
          background: #0c1226;
          border-color: #2a3158;
          color: #e5e7eb;
        }
        html.dark .promo-modal__field input.field-disabled {
          background: #131833;
          color: #64748b;
        }
        html.dark .field-hint { color: #8ea1b8; }
        html.dark .input-suffix {
          background: #131833;
          border-color: #2a3158;
          color: #aebdce;
        }
        html.dark .cycle-toggle-btn {
          background: #0c1226;
          border-color: #2a3158;
          color: #cbd5e1;
        }
        html.dark .cycle-toggle-btn:hover { border-color: #818cf8; color: #a5b4fc; }
        html.dark .cycle-toggle-btn--all {
          background: rgba(139, 92, 246, 0.18);
          border-color: #818cf8;
          color: #a5b4fc;
        }
        html.dark .toggle-label {
          color: #cbd5e1;
          border-color: #2a3158;
        }
        html.dark .toggle-label:hover { background: #151c38; border-color: #384070; }
        html.dark .promo-modal__actions { border-top-color: #2a3158; }
      `}</style>
    </div>
  );
};

export default PromocodesList;